import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../auth/useUser";
import {
  useGlobalRunStore,
  isTerminalRun,
  runKey,
  type ActiveRun,
} from "../hooks/useGlobalRunStore";
import { useProducts, productName } from "../hooks/useProducts";
import { titledRunEndLine } from "../lib/runNotify";

// RunMenu (0027): the persistent, cross-page run-awareness surface, living
// in the header next to "settings" (replaces the floating bottom-right
// pill, which overlapped page content). An activity glyph with an unread
// badge; clicking opens a notification-center popout listing in-flight +
// just-finished runs, each with a relative timestamp and a view action.
//
// Unread model (mirrors a typical notification center): a finished run is
// "unread" until the founder views it or hits "Mark all read" — opening the
// panel alone does NOT clear it, so the dots persist while they scan.
//
// Reads the global run store, so it reflects runs started on any page.
// Renders nothing when there are no runs to show.

type Bucket = "running" | "ready" | "failed";

function bucketOf(r: ActiveRun): Bucket {
  switch (r.snapshot.phase) {
    case "complete":
      return "ready";
    case "error":
      return "failed";
    default:
      return "running"; // connecting / running / reconnecting
  }
}

function relativeTime(ts: number): string {
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 45) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export function RunMenu() {
  const { data: user } = useUser();
  const navigate = useNavigate();
  const runs = useGlobalRunStore((s) => s.runs);
  const seen = useGlobalRunStore((s) => s.seenRunIds);
  const markRunsSeen = useGlobalRunStore((s) => s.markRunsSeen);
  const endRun = useGlobalRunStore((s) => s.endRun);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const productsQ = useProducts();

  // Close the popout on outside-click or Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!user) return null;
  const all = Object.values(runs);

  const runningCount = all.filter((r) => bucketOf(r) === "running").length;
  const unread = all.filter((r) => isTerminalRun(r.snapshot) && !seen[runKey(r)]);

  const nameFor = (pid: string) => {
    const n = productName(productsQ.data?.products, pid);
    return n.length > 32 ? n.slice(0, 31) + "…" : n;
  };

  const go = (r: ActiveRun) => {
    setOpen(false);
    // Only a finished row is read when viewed: a running row viewed now still
    // counts as unread once it ends, as the bar's working line promises
    // ("we'll tell you when it's ready", 0051 slice 2).
    if (isTerminalRun(r.snapshot)) markRunsSeen([runKey(r)]);
    // Every row lands on the product page: the bar there says how the pass
    // stands (0051 slice 2: the watch-it view is gone, async-first).
    navigate(`/products/${r.productId}`);
  };

  // Sort running → ready → failed for the popout list.
  const orderRank: Record<Bucket, number> = { running: 0, ready: 1, failed: 2 };
  const sorted = [...all].sort((a, b) => orderRank[bucketOf(a)] - orderRank[bucketOf(b)]);

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={
          unread.length > 0
            ? `Notifications — ${unread.length} unread`
            : runningCount > 0
              ? `Notifications — ${runningCount} running`
              : "Notifications"
        }
        aria-expanded={open}
        className="relative flex items-center text-ink-400 hover:text-ink-200 transition-colors"
      >
        <BellIcon active={runningCount > 0} />
        {unread.length > 0 ? (
          <span className="absolute -right-2 -top-1.5 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-accent px-1 text-[9px] font-bold text-ink-950">
            {unread.length}
          </span>
        ) : runningCount > 0 ? (
          <span className="absolute -right-1 -top-1 inline-flex h-2 w-2">
            <span className="absolute inline-flex h-2 w-2 rounded-full bg-accent opacity-50 animate-calm-pulse" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-accent" />
          </span>
        ) : null}
      </button>

      {open && (
        <div className="absolute right-0 top-8 z-30 w-80 overflow-hidden rounded-xl border border-ink-800 bg-ink-950/95 shadow-2xl backdrop-blur-sm animate-fade-in">
          <div className="flex items-center justify-between gap-2 border-b border-ink-800 px-4 py-3">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-ink-50">Notifications</span>
              {unread.length > 0 && (
                <span className="rounded-md bg-accent/15 px-1.5 py-0.5 text-[10px] font-bold tabular-nums text-accent">
                  {unread.length}
                </span>
              )}
            </div>
            {unread.length > 0 && (
              <button
                type="button"
                onClick={() => markRunsSeen(unread.map(runKey))}
                className="text-[11px] text-ink-400 hover:text-ink-200"
              >
                Mark all read
              </button>
            )}
          </div>

          {sorted.length === 0 && (
            <div className="px-4 py-8 text-center text-xs text-ink-400">
              You're all caught up.
            </div>
          )}
          <ul className="max-h-96 divide-y divide-ink-800/70 overflow-y-auto">
            {sorted.map((r) => {
              const b = bucketOf(r);
              const isUnread = isTerminalRun(r.snapshot) && !seen[runKey(r)];
              // 0159 F4: only a failed run says "failed"; a client give-up and
              // a server-reported interruption read their ruled line, titled.
              // "Needs attention" holds for all three.
              const endLine = b === "failed" ? titledRunEndLine(nameFor(r.productId), r.snapshot) : null;
              const title =
                b === "running"
                  ? `Working on “${nameFor(r.productId)}”`
                  : b === "ready"
                    ? `“${nameFor(r.productId)}” is ready`
                    : endLine ?? `“${nameFor(r.productId)}” failed`;
              const meta =
                b === "running"
                  ? "In progress"
                  : `${relativeTime(r.updatedAt)} · ${b === "ready" ? "Ready to review" : "Needs attention"}`;
              return (
                <li
                  key={runKey(r)}
                  className={`group relative flex items-start gap-3 px-4 py-3 hover:bg-ink-900/60 ${
                    isUnread ? "bg-ink-900/30" : ""
                  }`}
                >
                  <StatusBadge bucket={b} />
                  <button
                    type="button"
                    onClick={() => go(r)}
                    className="min-w-0 flex-1 text-left"
                  >
                    {/* A ruled end line wraps rather than truncates: it is a
                        sentence the founder must read whole. */}
                    <p className={`${endLine ? "break-words" : "truncate"} text-[13px] font-medium text-ink-100`}>{title}</p>
                    <p className="mt-0.5 text-[11px] text-ink-400">{meta}</p>
                    <span className="mt-1 inline-block text-[11px] font-medium text-accent group-hover:text-accent-glow">
                      View →
                    </span>
                  </button>
                  {isUnread && (
                    <span
                      className="mt-1.5 inline-block h-2 w-2 shrink-0 rounded-full bg-accent"
                      aria-label="unread"
                    />
                  )}
                  {isTerminalRun(r.snapshot) && (
                    <button
                      type="button"
                      aria-label="Dismiss"
                      onClick={() => endRun(r.productId)}
                      className="absolute right-2 top-2 text-ink-600 opacity-0 transition-opacity hover:text-ink-300 group-hover:opacity-100"
                    >
                      ✕
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

function BellIcon({ active }: { active: boolean }) {
  return (
    <svg
      viewBox="0 0 16 16"
      className={`h-[18px] w-[18px] ${active ? "text-accent" : ""}`}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M8 1.5a3.5 3.5 0 0 0-3.5 3.5c0 3-1.5 4-1.5 5h10c0-1-1.5-2-1.5-5A3.5 3.5 0 0 0 8 1.5Z" />
      <path d="M6.5 13a1.5 1.5 0 0 0 3 0" />
    </svg>
  );
}

function StatusBadge({ bucket }: { bucket: Bucket }) {
  if (bucket === "running") {
    return (
      <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent/15" aria-hidden="true">
        <span className="relative inline-flex h-2.5 w-2.5 items-center justify-center">
          <span className="absolute inline-flex h-2.5 w-2.5 rounded-full bg-accent opacity-50 animate-calm-pulse" />
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-accent" />
        </span>
      </span>
    );
  }
  if (bucket === "ready") {
    return (
      <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-ok-solid/20 text-ok" aria-hidden="true">
        <svg viewBox="0 0 12 12" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M2.5 6.5 L5 9 L9.5 3.5" />
        </svg>
      </span>
    );
  }
  return (
    <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-danger-solid/20 text-sm font-bold text-danger" aria-hidden="true">
      !
    </span>
  );
}
